import React from 'react';
import DOMPurify from 'isomorphic-dompurify';

interface FormattedSegment {
  text: string;
  format: {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
  };
}

// Parse markdown-style formatting
export function parseFormattedContent(content: string): FormattedSegment[] {
  const segments: FormattedSegment[] = [];
  let currentText = '';
  let currentFormat = {
    bold: false,
    italic: false,
    underline: false
  };
  let i = 0;

  const pushCurrentSegment = () => {
    if (currentText) {
      segments.push({ 
        text: currentText, 
        format: { ...currentFormat } 
      });
      currentText = '';
    }
  };

  while (i < content.length) {
    if (i + 1 < content.length && content[i] === '*' && content[i + 1] === '*') {
      pushCurrentSegment();
      currentFormat.bold = !currentFormat.bold;
      i += 2;
      continue;
    }
    
    if (i + 1 < content.length && content[i] === '_' && content[i + 1] === '_') {
      pushCurrentSegment();
      currentFormat.underline = !currentFormat.underline;
      i += 2;
      continue;
    }
    
    if (content[i] === '*') {
      pushCurrentSegment();
      currentFormat.italic = !currentFormat.italic;
      i++;
      continue;
    }

    currentText += content[i];
    i++;
  }

  pushCurrentSegment();
  return segments;
}

// Convert markdown to HTML
function markdownToHtml(content: string): string {
  // Replace markdown with HTML tags
  let html = content
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/__(.*?)__/g, '<u>$1</u>')
    .replace(/\n/g, '<br>');

  // Sanitize HTML
  return DOMPurify.sanitize(html);
}

// Format content for specific platforms
export function formatContentForPlatform(content: string, platform: string): string {
  switch (platform.toLowerCase()) {
    case 'facebook':
      // Keep markdown for Facebook - it will be converted to Unicode later
      return content;
    case 'instagram':
    case 'instagram_direct':
      // Remove formatting and convert HTML line breaks for Instagram
      return content
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/\*\*/g, '')
        .replace(/\*/g, '')
        .replace(/__/g, '');
    case 'linkedin':
      // Convert to HTML for LinkedIn
      return markdownToHtml(content);
    default:
      return content;
  }
}

interface FormattedTextProps {
  content: string;
  className?: string;
  useHtml?: boolean;
}

export function FormattedText({ content, className = '', useHtml = false }: FormattedTextProps) {
  if (useHtml) {
    return (
      <div 
        className={`whitespace-pre-wrap ${className}`}
        dangerouslySetInnerHTML={{ __html: markdownToHtml(content) }}
      />
    );
  }

  const segments = parseFormattedContent(content);
  
  return (
    <div className={`whitespace-pre-wrap ${className}`}>
      {segments.map((segment, index) => {
        let styles = [];
        if (segment.format.bold) styles.push('font-bold');
        if (segment.format.italic) styles.push('italic');
        if (segment.format.underline) styles.push('underline');
        
        return (
          <span key={index} className={styles.join(' ')}>
            {segment.text}
          </span>
        );
      })}
    </div>
  );
}